// fetch 的薄包裝。所有呼叫走相對路徑 /quote/api/...,自動帶 cookie。
// 樂觀鎖衝突(409)丟 ConflictError,讓編輯器可以單獨處理「別人已改過」。

import type { StatusOption } from "./types";

/** API 根路徑。前端掛在 /quote/ 底下,一律用相對路徑,不寫死域名。 */
export const API_BASE = "/quote/api";

/**
 * 後端錯誤回應的通用形狀。
 *
 * `detail` 有三種:一句字串(大部分的端點)、FastAPI 驗證錯誤的陣列,以及
 * 信箱那幾支的 `{code, message}` —— 前端靠 `code` 決定畫面(見 `ApiError.code`),
 * 給人看的是 `message`。
 */
export interface ApiErrorBody {
  detail?: string | Array<{ msg?: string }> | { code?: string; message?: string };
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | null;

  constructor(status: number, message: string, body: ApiErrorBody | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }

  /** 結構化錯誤的代碼(例 `MAILBOX_UNREACHABLE`)。字串 / 陣列形式的 `detail` 沒有代碼,回 `null`。 */
  get code(): string | null {
    const detail = this.body?.detail;
    if (detail && typeof detail === "object" && !Array.isArray(detail) && typeof detail.code === "string") {
      return detail.code;
    }
    return null;
  }
}

/** 401:尚未登入或 session 過期。 */
export class UnauthorizedError extends ApiError {
  constructor(message: string, body: ApiErrorBody | null) {
    super(401, message, body);
    this.name = "UnauthorizedError";
  }
}

/**
 * session 過期時的統一處理。由 AuthProvider 註冊。
 *
 * 401 不是某一頁自己的事:任何一支呼叫收到它,都代表這個瀏覽器已經不被後端
 * 認得了。只在原地顯示一句錯誤,使用者會卡在一個看起來還登入著、卻什麼都做
 * 不了的畫面上,連「重新登入」的入口都沒有。所以一律往上通報,由守衛導回登入頁。
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/**
 * 自己處理 401 的端點白名單。
 *
 * 這幾支用 401 表達的是「你送來的密碼不對」,不是「你的 session 過期了」——
 * 往上通報全域處理常式的話,使用者會被登出並導回 /login,而伺服器端那顆
 * cookie 其實還完全有效:
 *
 * - `/auth/login` —— 帳密不對,那是登入表單要顯示的錯誤。
 * - `/auth/change-password` —— **目前密碼**不正確。打錯一個字就被踢出去,
 *   對話框連同剛打的三個欄位一起消失,使用者只知道「我想改密碼,結果被登出了」,
 *   而且無從判斷密碼到底改了沒。
 * - `/users/me/profile` —— 改 Email 時附的**目前密碼**不正確,同上一條的理由。
 *   (同一支的 GET 也因此不通報;session 真的過期時,下一支別的呼叫就會導回登入頁。)
 *
 * 名單刻意逐一列出而不是放寬成「全部不通報」:session 真的過期時仍然要導回
 * 登入頁,否則使用者會卡在一個看起來還登入著、卻什麼都做不了的畫面上。
 * 日後新增同類端點,請加進這份名單。
 */
const SELF_HANDLED_401_PATHS = ["/auth/login", "/auth/change-password", "/users/me/profile"];

function handlesItsOwnUnauthorized(path: string): boolean {
  return SELF_HANDLED_401_PATHS.some((prefix) => path.startsWith(prefix));
}

/** 409:version 不符,這份報價單已被別人改過。 */
export class ConflictError extends ApiError {
  constructor(message: string, body: ApiErrorBody | null) {
    super(409, message, body);
    this.name = "ConflictError";
  }
}

/** 把後端的錯誤內容整理成一句可以直接顯示的中文訊息。 */
function extractMessage(status: number, body: ApiErrorBody | null): string {
  const detail = body?.detail;
  if (typeof detail === "string" && detail.trim() !== "") return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => d?.msg).filter((m): m is string => typeof m === "string");
    if (msgs.length > 0) return msgs.join("、");
  } else if (detail && typeof detail === "object") {
    const message = detail.message;
    if (typeof message === "string" && message.trim() !== "") return message;
  }
  if (status === 401) return "請先登入";
  if (status === 403) return "沒有權限執行這個動作";
  if (status === 404) return "找不到資料";
  if (status === 409) return "這份報價單已被其他人修改,請重新載入後再存檔";
  if (status >= 500) return "伺服器發生錯誤,請稍後再試";
  return `請求失敗(HTTP ${status})`;
}

/**
 * 查詢字串:略過 undefined / null / 空字串。
 *
 * 值可以是陣列,那時同一個鍵會**重複帶**(`?a=1&a=2`)而不是串成一個字串。
 * 多選的名稱類條件需要這一種:客戶名稱本來就可能有逗號(`Acme, Inc.`),
 * 用逗號串起來的話,後端一切,那家客戶就永遠篩不到。
 * 空陣列與陣列裡的空字串一律略過 —— 那是「沒有這個條件」,
 * 帶一個空值上去會讓後端去找一個名字是空字串的東西,畫面一筆都不剩。
 */
export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue | QueryValue[]>;

export function buildQuery(params?: QueryParams): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === "") continue;
        search.append(key, String(item));
      }
      continue;
    }
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs === "" ? "" : `?${qs}`;
}

function toUrl(path: string, params?: QueryParams): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE}${clean}${buildQuery(params)}`;
}

async function readBody(res: Response): Promise<ApiErrorBody | null> {
  const type = res.headers.get("content-type") ?? "";
  if (!type.includes("application/json")) return null;
  try {
    return (await res.json()) as ApiErrorBody;
  } catch {
    return null;
  }
}

async function request<T>(method: string, path: string, init: RequestInit = {}, params?: QueryParams): Promise<T> {
  const res = await fetch(toUrl(path, params), {
    ...init,
    method,
    // session cookie 自動帶上。
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      // **FormData 一個字都不准設 Content-Type。** multipart 的
      // `boundary=----WebKitFormBoundary...` 是 fetch 自己產生的,寫死一個
      // `multipart/form-data` 上去,boundary 就掉了 —— 後端照著標頭去切,
      // 切不出任何欄位,收到的是一個空的檔案。症狀是「傳上去什麼都沒有」,
      // 而標頭上完全看不出哪裡錯。這是上傳最常踩的一個坑。
      ...(init.body === undefined || init.body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      ...(init.headers ?? {}),
    },
  });

  if (!res.ok) {
    const body = await readBody(res);
    const message = extractMessage(res.status, body);
    if (res.status === 409) throw new ConflictError(message, body);
    if (res.status === 401) {
      if (!handlesItsOwnUnauthorized(path)) onUnauthorized?.();
      throw new UnauthorizedError(message, body);
    }
    throw new ApiError(res.status, message, body);
  }

  // 204 No Content 沒有內容可解。
  if (res.status === 204) return undefined as T;
  const type = res.headers.get("content-type") ?? "";
  if (!type.includes("application/json")) return (await res.text()) as unknown as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, params?: QueryParams): Promise<T> => request<T>("GET", path, {}, params),

  post: <T>(path: string, body?: unknown, params?: QueryParams): Promise<T> =>
    request<T>("POST", path, body === undefined ? {} : { body: JSON.stringify(body) }, params),

  put: <T>(path: string, body?: unknown, params?: QueryParams): Promise<T> =>
    request<T>("PUT", path, body === undefined ? {} : { body: JSON.stringify(body) }, params),

  /** 部分更新:只送要改的欄位,沒送到的維持原值(使用者管理的改角色 / 停用走這條)。 */
  patch: <T>(path: string, body?: unknown, params?: QueryParams): Promise<T> =>
    request<T>("PATCH", path, body === undefined ? {} : { body: JSON.stringify(body) }, params),

  /**
   * DELETE。**第二個參數是 body,不是 params** —— 要帶查詢字串請寫
   * `api.del(path, undefined, params)`。
   *
   * 會帶 body 是因為刪除報價單要把單號打對才刪得掉(規格 §4):那個確認值只有
   * 前端擋的話,用 curl 就繞過去了,所以它得真的送到後端去比對。
   */
  del: <T = void>(path: string, body?: unknown, params?: QueryParams): Promise<T> =>
    request<T>("DELETE", path, body === undefined ? {} : { body: JSON.stringify(body) }, params),

  /**
   * multipart 上傳。**不要拿 `api.post` 傳檔案** —— 那一支送的是
   * `JSON.stringify(body)`,`FormData` 進去只會變成 `"[object FormData]"`。
   *
   * 呼叫端自己組 `FormData`(欄位名要與後端的參數名一致,附件那一支是 `file`),
   * 這裡只負責送出去;**Content-Type 交給 fetch 產生**(見 `request`),
   * 手動設會把 multipart 的 boundary 弄掉。
   *
   * 其餘與 `api.post` 完全一樣:401 照樣往上通報、錯誤照樣整理成中文訊息 ——
   * 上傳被 415 / 413 擋下來時,後端那一句「只收得下 PDF、JPEG…」要原樣
   * 送到畫面上,使用者才知道下一步該做什麼。
   */
  upload: <T>(path: string, form: FormData, params?: QueryParams): Promise<T> =>
    request<T>("POST", path, { body: form }, params),

  /** 同 `upload`,但用 PUT(存草稿這種「覆蓋上一版」的 multipart)。 */
  uploadPut: <T>(path: string, form: FormData, params?: QueryParams): Promise<T> =>
    request<T>("PUT", path, { body: form }, params),

  /** 匯出用:回傳原始 Blob,交給呼叫端做下載。 */
  blob: async (path: string, params?: QueryParams): Promise<Blob> => {
    const res = await fetch(toUrl(path, params), { credentials: "same-origin" });
    if (!res.ok) {
      const body = await readBody(res);
      const message = extractMessage(res.status, body);
      if (res.status === 401) {
        onUnauthorized?.();
        throw new UnauthorizedError(message, body);
      }
      throw new ApiError(res.status, message, body);
    }
    return res.blob();
  },
};

/** 匯出檔案的絕對路徑,供 <a href> 直接使用。 */
export function exportUrl(quoteId: number, kind: "pdf" | "xlsx"): string {
  return `${API_BASE}/quotes/${quoteId}/export.${kind}`;
}

/**
 * 前端掛載的根路徑。整個站台掛在 `/quote/` 底下,`API_BASE` 去掉尾巴的
 * `/api` 就是它 —— 兩個地方各寫一次的話,搬家時一定會漏掉其中一個。
 */
const APP_ROOT = API_BASE.replace(/\/api$/, "");

/**
 * 把後端給的檔案路徑(`AttachmentOut.download_url`,例 `/api/files/11`)
 * 接上前端掛載的根路徑,變成 `<img src>` / `<a href>` 打得到的網址。
 *
 * **網址的形狀仍然由後端決定**(設計規格 §3.3:檔案一律經過認證的 API);
 * 這裡只補 `/quote` 那一段 —— 後端回的是它自己那一棵樹的絕對路徑,
 * 原樣放進 `src` 會打到 `https://test.livest.qzz.io/api/files/11`,
 * 那條路徑在 nginx 上根本不存在,畫面上就是一張破圖。
 *
 * 已經帶著根路徑的、或本來就是完整網址的,原樣回傳 —— 不然重複接一次
 * 會變成 `/quote/quote/api/...`。
 */
export function fileUrl(path: string): string {
  const clean = String(path ?? "").trim();
  if (clean === "") return "";
  if (/^https?:\/\//i.test(clean)) return clean;
  if (clean === APP_ROOT || clean.startsWith(`${APP_ROOT}/`)) return clean;
  return `${APP_ROOT}${clean.startsWith("/") ? "" : "/"}${clean}`;
}

/* ---------- 報價單狀態的中文字樣(第 13 輪,設計規格 §1.4) ---------- */

/**
 * 報價單狀態與它們的中文字樣,來自 `GET /api/quote-statuses`。
 *
 * **後端是唯一來源。** 前端自己維護一份對照表的話,兩份遲早會漂開:第 13 輪
 * 從五個值擴成七個時,漏掉的那幾個會原封不動變成畫面上的英文代碼;
 * 而同一輪 `CANCELLED` 的字樣也從「已作廢」改成「已取消」,那種改動不會有
 * 任何地方提醒前端跟著改。所以狀態下拉的選項一律讀這一支。
 *
 * **答案存在模組層級的快取裡。** 它是封閉的列舉,一次登入期間不會變,而
 * 編輯器每開一張單就問一次,只是讓同一份答案在網路上多走幾趟。
 */
let quoteStatusCache: StatusOption[] | null = null;
let quoteStatusPending: Promise<StatusOption[]> | null = null;

/** 一列真的長得像狀態選項嗎?後端回了非預期的形狀時,下拉少幾個選項無妨,
    但整個編輯器不可以炸掉。 */
function isStatusOption(row: unknown): row is StatusOption {
  const item = row as { value?: unknown; label?: unknown } | null;
  return typeof item?.value === "string" && typeof item?.label === "string";
}

/**
 * 取得狀態選項,同一個分頁只會真的打一次 API。
 *
 * **失敗與空清單都不進快取**:一次網路抖動就讓這個分頁從此沒有狀態可選的話,
 * 使用者要重新整理整頁才救得回來,而他完全不知道自己為什麼要這麼做。
 */
export function loadQuoteStatuses(): Promise<StatusOption[]> {
  if (quoteStatusCache !== null) return Promise.resolve(quoteStatusCache);
  if (quoteStatusPending !== null) return quoteStatusPending;
  quoteStatusPending = api
    .get<StatusOption[]>("/quote-statuses")
    .then((rows) => {
      const options = Array.isArray(rows) ? rows.filter(isStatusOption) : [];
      quoteStatusCache = options.length > 0 ? options : null;
      quoteStatusPending = null;
      return options;
    })
    .catch((error: unknown) => {
      quoteStatusPending = null;
      throw error;
    });
  return quoteStatusPending;
}
